import type { FormEvent, MutableRefObject } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';

import type { TechnicalStatus } from '../shared/configurable';

export interface NewsletterFormValues {
  email: string;
  frequency: string;
  name: string;
}

interface NewsletterFormErrors {
  email?: string;
  name?: string;
}

interface NewsletterFormTouched {
  email: boolean;
  name: boolean;
}

export interface NewsletterFormCallbacks {
  onSubmit?: (values: NewsletterFormValues) => Promise<void> | void;
  onSuccess?: (values: NewsletterFormValues) => void;
  onError?: (error: unknown) => void;
  initialValues?: NewsletterFormValues;
}

const INITIAL_VALUES: NewsletterFormValues = { email: '', frequency: '1', name: '' };
const INITIAL_TOUCHED: NewsletterFormTouched = { email: false, name: false };
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MOCK_SUBMIT_DELAY_MS = 1200;

const validateNewsletterForm = (values: NewsletterFormValues): NewsletterFormErrors => {
  const errors: NewsletterFormErrors = {};

  if (values.name.trim().length < 2) {
    errors.name = 'El nombre debe tener al menos 2 caracteres.';
  }

  if (!EMAIL_REGEX.test(values.email.trim())) {
    errors.email = 'Ingresa un correo electrónico válido.';
  }

  return errors;
};

const clearTimer = (timerRef: MutableRefObject<ReturnType<typeof setTimeout> | null>) => {
  const timer = timerRef.current;
  if (!timer) return null;
  clearTimeout(timer);
  return null;
};

const useNewsletterForm = (callbacks?: NewsletterFormCallbacks) => {
  const initialValues = callbacks?.initialValues;
  const [values, setValues] = useState<NewsletterFormValues>(initialValues ?? INITIAL_VALUES);

  useEffect(() => {
    setValues(initialValues ?? INITIAL_VALUES);
    setTouched(INITIAL_TOUCHED);
  }, [initialValues]);
  const [touched, setTouched] = useState<NewsletterFormTouched>(INITIAL_TOUCHED);
  const [status, setStatus] = useState<TechnicalStatus>('idle');

  const submitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const errors = useMemo(() => validateNewsletterForm(values), [values]);

  const handleChange = (field: keyof NewsletterFormValues, value: string) => {
    setValues((prev) => ({ ...prev, [field]: value }));
    if (status === 'success' || status === 'error') {
      setStatus('idle');
    }
  };

  const handleBlur = (field: keyof NewsletterFormTouched) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const nextErrors = validateNewsletterForm(values);
    setTouched({ email: true, name: true });

    if (Object.keys(nextErrors).length > 0) {
      setStatus('idle');
      return;
    }

    submitTimerRef.current = clearTimer(submitTimerRef);
    setStatus('submitting');

    if (callbacks?.onSubmit) {
      const result = callbacks.onSubmit(values);
      const settled = result instanceof Promise ? result : Promise.resolve();

      settled
        .then(() => {
          setValues(INITIAL_VALUES);
          setTouched(INITIAL_TOUCHED);
          setStatus('success');
          callbacks.onSuccess?.(values);
        })
        .catch((error: unknown) => {
          setStatus('error');
          callbacks.onError?.(error);
        });
    } else {
      submitTimerRef.current = setTimeout(() => {
        setValues(INITIAL_VALUES);
        setTouched(INITIAL_TOUCHED);
        setStatus('success');
      }, MOCK_SUBMIT_DELAY_MS);
    }
  };

  const isInvalid = (field: keyof NewsletterFormErrors) => touched[field] && Boolean(errors[field]);

  useEffect(
    () => () => {
      submitTimerRef.current = clearTimer(submitTimerRef);
    },
    [],
  );

  return {
    errors,
    handleBlur,
    handleChange,
    handleSubmit,
    isInvalid,
    isSubmitting: status === 'submitting',
    status,
    values,
  };
};

export default useNewsletterForm;
